import { useQuery } from '@tanstack/react-query';
import type { AuditPageSummary } from '@seo/types';
import { apiGet } from '@/lib/api';

export interface AuditPageFilters {
  status_class?: number;
  has_issues?: boolean;
  q?: string;
  limit?: number;
}

export function useAuditPages(id: string | undefined, filters: AuditPageFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status_class) params.set('status_class', String(filters.status_class));
  if (filters.has_issues !== undefined) params.set('has_issues', String(filters.has_issues));
  if (filters.q) params.set('q', filters.q);
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery({
    queryKey: ['audit-pages', id, params.toString()],
    queryFn: () =>
      apiGet<{ items: AuditPageSummary[]; has_more: boolean }>(
        `/v1/audits/${id}/pages?${params.toString()}`,
      ),
    enabled: !!id,
  });
}

/**
 * Full page detail: parsed metadata + list of issues touching this page.
 */
export function useAuditPageDetail(
  auditId: string | undefined,
  pageId: string | undefined,
) {
  return useQuery({
    queryKey: ['audit-page', auditId, pageId],
    queryFn: () =>
      apiGet<{
        page: {
          id: string;
          url: string;
          title: string | null;
          statusCode: number | null;
          depth: number | null;
          discoveredFrom: string | null;
          wordCount: number | null;
          parsed: Record<string, unknown>;
          psi: Record<string, unknown> | null;
        };
        score: {
          criticalCount: number;
          highCount: number;
          mediumCount: number;
          lowCount: number;
          contentQualityScore: number | null;
          performanceScore: number | null;
        } | null;
        issues: Array<{
          id: string;
          checkId: string;
          severity: string;
          bucket: string;
          title: string;
          summary: string;
          affectedUrlCount: number;
          llmExplanation: string | null;
          llmFix: string | null;
          llmFixCode: string | null;
          llmFixLang: string | null;
        }>;
      }>(`/v1/audits/${auditId}/pages/${pageId}`),
    enabled: !!auditId && !!pageId,
  });
}
